import ReactDOMServer from 'react-dom/server';
import { InfoboxProps, PushpinProps } from '../../types/MapModels';
import { VEHICLE_MARKER_KIND } from './AzureMap/AzureMapMarkerConstants';

interface VehicleInfoboxProps {
    vehicleId: string;
    description: string;
    cssColor?: string;
}

interface VehicleMapElementsOptions {
    location: CoordinatePair;
    vehicleId: string;
    description: string;
    cssColor?: string;
    icon?: string;
    catId?: string;
    onClick?: () => void;
    popupVariant?: 'plain' | 'html';
    showCloseButton?: boolean;
    offset?: [number, number];
}

export const DEFAULT_VEHICLE_POPUP_OFFSET: [number, number] = [0, -14];

export const COMPACT_VEHICLE_POPUP = {
    minWidth: 100,
    padding: '4px 6px',
    offset: [0, -30] as [number, number],
};

function VehicleInfobox(props: VehicleInfoboxProps) {
    const cssColor = props.cssColor || 'rgb(0, 64, 128)';
    return (
        <div className="bus-pushpin" style={{ borderColor: cssColor }}>
            <div className="bus-pinpoint"></div>
            <div className="bus-offset">
                <div className="bus-icon" style={{ backgroundColor: cssColor }}></div>
                <div className="bus-name"><strong>{props.vehicleId}</strong><br /><small>{!props.description ? (<>&mdash;</>) : props.description}</small></div>
            </div>
        </div>
    );
}

export function getVehicleInfoboxHtml(vehicleId: string, description: string, cssColor?: string) {
    return ReactDOMServer.renderToStaticMarkup(
        <VehicleInfobox
            vehicleId={vehicleId}
            description={description}
            cssColor={cssColor}
        />);
}

export function createVehiclePushpin({
    location,
    icon = 'busBlue',
    catId = 'bus-pushpin',
    onClick,
}: Pick<VehicleMapElementsOptions, 'location' | 'icon' | 'catId' | 'onClick'>): PushpinProps {
    const eventHandlers = !onClick
        ? []
        : [{
            event: 'click' as const,
            callback: () => onClick(),
        }];

    return {
        location,
        options: {
            icon,
            catId,
            markerKind: VEHICLE_MARKER_KIND,
        },
        eventHandlers,
    };
}

export function createVehicleInfobox({
    location,
    vehicleId,
    description,
    cssColor,
    popupVariant = 'plain',
    showCloseButton = false,
    offset,
}: Omit<VehicleMapElementsOptions, 'icon' | 'catId'>): InfoboxProps {
    const popupOffset = offset ?? DEFAULT_VEHICLE_POPUP_OFFSET;

    if (popupVariant === 'html') {
        return {
            location,
            options: {
                htmlContent: getVehicleInfoboxHtml(vehicleId, description, cssColor),
                showCloseButton,
                offset: popupOffset,
            },
        };
    }

    return {
        location,
        options: {
            title: vehicleId,
            description,
            showCloseButton,
            offset: popupOffset,
        },
    };
}

export function createVehicleMapElements(options: VehicleMapElementsOptions): { pushpin: PushpinProps; infobox: InfoboxProps; } {
    return {
        pushpin: createVehiclePushpin(options),
        infobox: createVehicleInfobox(options),
    };
}
